/**
 * ShipmentMiniMap — Leaflet mini-map shown in the expanded MyShipments card
 * for in-transit shipments. Polls realtime-eta to display origin/destination
 * markers, the planned route polyline, the live current position, and a
 * smoothly-interpolated car marker between server ticks.
 *
 * Visual style: dark CartoCDN tiles to match BluMove's brand.
 */
import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, CircleMarker, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Path } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_ORIGIN = L.divIcon({
  className: "minimap-pin",
  html: `<div style="width:14px;height:14px;border-radius:50%;background:#22d3ee;border:2px solid #fff;box-shadow:0 0 8px rgba(34,211,238,0.7);"></div>`,
  iconSize: [14, 14], iconAnchor: [7, 7],
});

const ICON_DEST = L.divIcon({
  className: "minimap-pin",
  html: `<div style="width:14px;height:14px;border-radius:50%;background:#10b981;border:2px solid #fff;box-shadow:0 0 8px rgba(16,185,129,0.7);"></div>`,
  iconSize: [14, 14], iconAnchor: [7, 7],
});

// Heading-aware car icon with a glow halo and animated entry
const buildCarIcon = (headingDeg = 0) => L.divIcon({
  className: "minimap-car",
  html: `
    <div class="minimap-car-wrap" style="transform: rotate(${headingDeg}deg);">
      <div class="minimap-car-glow"></div>
      <svg class="minimap-car-svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M5 14l1.5-5a2 2 0 0 1 2-1.5h7a2 2 0 0 1 2 1.5L19 14v4h-2v-1H7v1H5v-4z" fill="#10b981" stroke="#0b3622" stroke-width="1"/>
        <circle cx="8" cy="16" r="1.5" fill="#0b3622"/>
        <circle cx="16" cy="16" r="1.5" fill="#0b3622"/>
        <rect x="8" y="9" width="8" height="3" rx="1" fill="#d1fae5" stroke="#0b3622" stroke-width="0.7"/>
      </svg>
    </div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

function bearingDeg(lat1, lng1, lat2, lng2) {
  const toRad = (d) => d * Math.PI / 180;
  const toDeg = (r) => r * 180 / Math.PI;
  const dLng = toRad(lng2 - lng1);
  const y = Math.sin(dLng) * Math.cos(toRad(lat2));
  const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2))
          - Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

export default function ShipmentMiniMap({ trackingCode, status }) {
  const [eta, setEta] = useState(null);
  const [carPos, setCarPos] = useState(null);  // smoothly-interpolated lat/lng
  const targetRef = useRef(null);              // {lat, lng, fromLat, fromLng, startedAt, durationMs}
  const rafRef = useRef(null);

  useEffect(() => {
    if (status !== "in_transit") return;
    let alive = true;
    const fetchEta = async () => {
      try {
        const r = await axios.get(`${API}/shipments/${trackingCode}/realtime-eta`);
        if (alive) setEta(r.data);
      } catch (e) { /* ignore */ }
    };
    fetchEta();
    const id = setInterval(fetchEta, 6000);
    return () => { alive = false; clearInterval(id); };
  }, [trackingCode, status]);

  // Whenever the server gives us a new current_coords, smoothly animate the
  // car from its previous position to the new one over `durationMs`.
  useEffect(() => {
    if (!eta?.current_coords) return;
    const next = eta.current_coords;
    const from = carPos || next;
    targetRef.current = {
      fromLat: from.lat, fromLng: from.lng,
      lat: next.lat, lng: next.lng,
      startedAt: performance.now(),
      durationMs: 5500,  // slightly less than the 6s poll interval
    };
    const tick = (now) => {
      const t = targetRef.current;
      if (!t) return;
      const k = Math.min(1, (now - t.startedAt) / t.durationMs);
      // ease-out cubic
      const e = 1 - Math.pow(1 - k, 3);
      setCarPos({
        lat: t.fromLat + (t.lat - t.fromLat) * e,
        lng: t.fromLng + (t.lng - t.fromLng) * e,
      });
      if (k < 1) rafRef.current = requestAnimationFrame(tick);
    };
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eta?.current_coords?.lat, eta?.current_coords?.lng]);

  if (status !== "in_transit") return null;
  if (!eta || !eta.in_simulation || !eta.origin_coords || !eta.destination_coords) return null;

  const o = eta.origin_coords;
  const d = eta.destination_coords;
  const car = carPos || eta.current_coords;
  const heading = bearingDeg(o.lat, o.lng, d.lat, d.lng);
  const bounds = [[o.lat, o.lng], [d.lat, d.lng]];

  // Two-segment route: behind (origin → car, solid green) and ahead (car → dest, dashed grey)
  const segDone = car ? [[o.lat, o.lng], [car.lat, car.lng]] : null;
  const segLeft = car ? [[car.lat, car.lng], [d.lat, d.lng]] : null;

  return (
    <div data-testid={`minimap-${trackingCode}`} style={{ marginTop: 16, marginBottom: 8 }}>
      <div className="flex items-center gap-2 mb-2">
        <Path size={14} weight="duotone" style={{ color: "var(--primary)" }} />
        <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Localizacao em tempo real · {eta.progress_pct}% do trajeto
        </span>
      </div>
      <div style={{ height: 220, borderRadius: 12, overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)", background: "#0f1115" }}>
        <MapContainer
          bounds={bounds}
          boundsOptions={{ padding: [30, 30] }}
          scrollWheelZoom={false}
          className="minimap-dark"
          style={{ height: "100%", width: "100%", background: "#0f1115" }}
          attributionControl={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            subdomains="abc"
            maxZoom={19}
          />
          {/* Trail already covered (origin → car) */}
          {segDone && (
            <Polyline
              positions={segDone}
              pathOptions={{ color: "#10b981", weight: 3, opacity: 0.9 }}
            />
          )}
          {/* Path ahead (car → dest) */}
          {segLeft && (
            <Polyline
              positions={segLeft}
              pathOptions={{ color: "#22d3ee", weight: 2, opacity: 0.45, dashArray: "5 7" }}
            />
          )}
          {/* Origin */}
          <Marker position={[o.lat, o.lng]} icon={ICON_ORIGIN}>
            <Tooltip direction="top" offset={[0, -6]} className="minimap-tooltip">{eta.origin}</Tooltip>
          </Marker>
          {/* Destination */}
          <Marker position={[d.lat, d.lng]} icon={ICON_DEST}>
            <Tooltip direction="top" offset={[0, -6]} className="minimap-tooltip">{eta.destination}</Tooltip>
          </Marker>
          {/* Animated car */}
          {car && (
            <>
              <CircleMarker
                center={[car.lat, car.lng]}
                radius={16}
                pathOptions={{ color: "#10b981", fillColor: "#10b981", fillOpacity: 0.12, weight: 0 }}
              />
              <Marker position={[car.lat, car.lng]} icon={buildCarIcon(heading)}>
                <Tooltip direction="top" offset={[0, -8]} className="minimap-tooltip">
                  {eta.remaining_minutes < 1 ? "<1 min" : `${Math.round(eta.remaining_minutes)} min · ${eta.speed_kmh} km/h`}
                </Tooltip>
              </Marker>
            </>
          )}
        </MapContainer>
      </div>
    </div>
  );
}
